import {
  CLOUD_LICENCE_REFUSAL_EVENT,
  cloudLicenceMessage,
  isCloudRestrictionCode,
} from '@application/licence/CloudLicenceMessages';
import { resilientFetch } from '@infra/network/resilientFetch';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { logger } from '@infra/telemetry/Logger';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { isRecetteBuild, requireRecetteApiBase } from '@shared/constants/buildProfile';
import { resolveHttpsApiBase } from '@shared/utils/httpsApiUrl';
import type { Role } from '@shared/types/User';
import { SyncRequestError } from '@infra/sync/hybrid/SyncPorts';
import { SYNC_PROTOCOL_HEADER, SYNC_PROTOCOL_VERSION } from '@infra/sync/hybrid/SyncProtocol';

const DEFAULT_DEV_BUSINESS_API_URL = 'http://localhost:3002';
const DEFAULT_PROD_BUSINESS_API_URL = 'https://business.nexxus.ga';
/** Cookie CSRF du navigateur Cloud (lisible par le script, valeur dérivée de la session côté serveur). */
const CSRF_COOKIE_NAME = '__Host-nexxus_csrf';
/** Annonce au serveur que le jeton de session reste dans le cookie httpOnly (jamais dans le corps). */
const SESSION_MODE_HEADER = 'X-Session-Mode';
/** Événement émis quand la session Cloud navigateur n'est plus valable (expirée, révoquée, cookie absent). */
export const CLOUD_SESSION_EXPIRED_EVENT = 'nexxus:cloud-session-expired';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const SESSION_GONE_CODES = new Set(['session_invalid', 'user_token_required', 'installation_token_required']);

function aujourdhuiIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Ancienne clé localStorage — purgée au boot (SEC C1 : session = cookie httpOnly). */
const USER_TOKEN_STORAGE_KEY = 'nexxus_business_user_token';

export class BusinessApiConflictError extends Error {
  constructor(message = 'Conflit de modification — recharger les données.') {
    super(message);
    this.name = 'BusinessApiConflictError';
  }
}

/** Classe de reprise indiquée par le serveur dans le champ `retry` des erreurs. */
export type ApiRetryClass = 'permanent' | 'transient' | 'conflict';

/** Entreprise proposée au choix à la connexion (cabinet : même e-mail et même mot de passe dans plusieurs entreprises). */
export interface WebCompany {
  tenant_id: string;
  name: string;
}

export class BusinessApiError extends Error {
  constructor(
    message: string,
    readonly statusCode?: number,
    readonly code?: string,
    readonly retry?: ApiRetryClass,
    /** Champs supplémentaires de la réponse d'erreur (ex. `companies` pour `tenant_required`). */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'BusinessApiError';
  }
}

const INSTALLATION_AUTH_CODES = new Set([
  'installation_invalid',
  'installation_token_required',
  'installation_expired',
]);

/** True si l’échec vient du jeton d’installation (renouvelable), pas d’un refus utilisateur. */
export function isInstallationAuthFailure(err: unknown): boolean {
  if (!(err instanceof BusinessApiError)) return false;
  if (err.statusCode !== 401) return false;
  if (err.code && INSTALLATION_AUTH_CODES.has(err.code)) return true;
  return err.message.includes('Installation non autorisée');
}

/** Marqueur build Cloud SEC C1 (cookie httpOnly, pas de JWT en localStorage). */
export const SEC_C1_BUILD_MARKER = 'SEC-C1-split1';

interface ApiEntityRow {
  id: string;
  tenant_id: string;
  payload: Record<string, unknown>;
  version: number;
}

interface RemoteUser {
  id: string;
  tenant_id: string;
  email: string;
  display_name: string;
  role: string;
}

/** Droits de l'installation renvoyés par GET /v1/auth/me (licence résolue côté serveur). */
export interface RemoteInstallationInfo {
  tenant_id: string;
  features?: string[];
  modules?: string[];
  tier?: string;
  /** 0 = illimité. */
  max_users?: number;
  /** 0 = illimité. */
  max_salaries?: number;
  license_status?: string;
  license_expires_at?: string;
}

/** Utilisateur renvoyé par /v1/users (liste / création). */
export interface RemoteUserRecord extends RemoteUser {
  active?: boolean;
  created_at?: string;
  /** Fiche salarié rattachée, quand l'API la porte. */
  salarie_id?: string | null;
}

/** Entrée d'audit renvoyée par /v1/audit-logs. */
export interface RemoteAuditLogRecord {
  id: string;
  tenant_id: string;
  created_at: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  metadata: Record<string, unknown> | null;
}

export interface RemoteVersionedPayload {
  tenant_id: string;
  payload: Record<string, unknown>;
  version: number;
  updated_at?: string;
}

export interface RemoteModuleDataRecord {
  namespace: string;
  tenant_id: string;
  payload: Record<string, unknown>;
  version: number;
  updated_at?: string;
}

export interface RemoteCoffrefortDocument {
  id: string;
  tenant_id: string;
  name: string;
  size: number;
  scope: string;
  salarie_id: string | null;
  category: string;
  mime_type: string | null;
  uploaded_at: string;
}

/** Client HTTP vers nexxus-business-api (mode distant). */
export class BusinessApiClient {
  private online = false;
  private lastPingAt = 0;
  private readonly pingTtlMs = 15_000;
  private static networkWired = false;
  /** Repli mémoire uniquement (Electron / tests) — jamais localStorage (SEC C1). */
  private memoryUserToken: string | null = null;
  /** Session établie (cookie httpOnly et/ou jeton mémoire) — survit au refresh via /me. */
  private userSessionActive = false;
  private static legacyTokenPurged = false;
  /** Cloud navigateur : renouvellement planifié de la session (POST /v1/auth/refresh avant l'échéance). */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshDueAt = 0;
  private sessionExpiresAt = 0;
  private visibilityWired = false;

  /**
   * `baseUrl` et `installationToken` ne servent qu'aux bancs d'intégration (plusieurs postes dans un même
   * processus, serveur derrière un proxy) ; en production, aucun des deux n'est fourni.
   */
  constructor(
    private readonly overrides: { baseUrl?: string; installationToken?: () => Promise<string | null> } = {},
  ) {}

  static wireNetworkListener(client: BusinessApiClient): void {
    if (BusinessApiClient.networkWired || typeof window === 'undefined') return;
    BusinessApiClient.networkWired = true;
    NetworkStatusService.subscribe((browserOnline) => {
      if (!browserOnline) client.markOffline();
    });
  }

  /** Purge l’ancien jeton XSS-accessible une seule fois par onglet. */
  private static purgeLegacyStoredTokens(): void {
    if (BusinessApiClient.legacyTokenPurged) return;
    BusinessApiClient.legacyTokenPurged = true;
    try {
      localStorage.removeItem(USER_TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(USER_TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Jeton CSRF du cookie `__Host-nexxus_csrf` (null hors navigateur Cloud ou sans session). */
  private static readCsrfToken(): string | null {
    if (typeof document === 'undefined') return null;
    for (const part of document.cookie.split(';')) {
      const eq = part.indexOf('=');
      if (eq > 0 && part.slice(0, eq).trim() === CSRF_COOKIE_NAME) {
        try {
          return decodeURIComponent(part.slice(eq + 1).trim());
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  getBaseUrl(): string | null {
    BusinessApiClient.purgeLegacyStoredTokens();
    if (this.overrides.baseUrl) return this.overrides.baseUrl;
    // Cloud navigateur : même origine que le site (le proxy du site transmet /v1/* à business-api).
    if (isBrowserCloudHost() && typeof window !== 'undefined') return window.location.origin;
    const fromEnv = import.meta.env.VITE_BUSINESS_API_URL as string | undefined;
    if (isRecetteBuild()) return requireRecetteApiBase(fromEnv, 'VITE_BUSINESS_API_URL');
    const allowLocal = Boolean(import.meta.env.DEV);
    const fallback = allowLocal ? DEFAULT_DEV_BUSINESS_API_URL : DEFAULT_PROD_BUSINESS_API_URL;
    return resolveHttpsApiBase(fromEnv, fallback, allowLocal);
  }

  isOnline(): boolean {
    if (!NetworkStatusService.isBrowserOnline()) return false;
    return this.online;
  }

  markOffline(): void {
    this.online = false;
    this.lastPingAt = 0;
  }

  /** Jeton mémoire pour Bearer (repli) — null après refresh SPA si seule la cookie reste. */
  getUserToken(): string | null {
    BusinessApiClient.purgeLegacyStoredTokens();
    return this.memoryUserToken;
  }

  /** True si login OK ou /me cookie OK — à utiliser pour les gardes sync, pas comme Bearer. */
  hasUserSession(): boolean {
    BusinessApiClient.purgeLegacyStoredTokens();
    // Référence volontaire : garde le marqueur SEC C1 dans le bundle minifié.
    void SEC_C1_BUILD_MARKER;
    return this.userSessionActive || Boolean(this.memoryUserToken);
  }

  setUserToken(token: string | null): void {
    BusinessApiClient.purgeLegacyStoredTokens();
    this.memoryUserToken = token;
    this.userSessionActive = Boolean(token);
    /** SEC C1 : à chaque logout / clear, purger aussi un éventuel jeton legacy réécrit. */
    if (token === null) {
      try {
        localStorage.removeItem(USER_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(USER_TOKEN_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }

  /** Marque la session active après succès cookie-only (ex. refresh /me). */
  acknowledgeUserSession(): void {
    this.userSessionActive = true;
  }

  async confirmWriteReady(): Promise<boolean> {
    if (!NetworkStatusService.isBrowserOnline()) {
      this.online = false;
      return false;
    }
    const base = this.getBaseUrl();
    // Cloud navigateur : la session est un cookie, il n'y a pas de jeton d'installation.
    const installation = isBrowserCloudHost()
      ? null
      : await SecureLicenceStore.getInstallationSessionToken();
    if (!base || (!installation && !isBrowserCloudHost())) {
      this.online = false;
      return false;
    }

    const now = Date.now();
    if (now - this.lastPingAt < this.pingTtlMs) return this.online;

    try {
      const res = await resilientFetch(`${base}/health`, { method: 'GET' }, { timeoutMs: 5_000 });
      this.online = res.ok;
      this.lastPingAt = now;
      if (!res.ok) logger.warn({ event: 'business_api_unhealthy' });
      return this.online;
    } catch {
      this.online = false;
      this.lastPingAt = now;
      logger.warn({ event: 'business_api_unreachable' });
      return false;
    }
  }

  /**
   * C5 — bootstrap accessible à tous les rôles auth.
   * Avec session : `role` + `features` adaptés ; sans user : 1er admin uniquement.
   */
  async getBootstrapStatus(): Promise<{
    has_users: boolean;
    needs_first_admin: boolean;
    is_activated?: boolean;
    user_id?: string;
    tenant_id?: string;
    cabinet_id?: string | null;
    role?: string;
    features?: string[];
  }> {
    return this.request('/v1/auth/bootstrap-status', { method: 'GET', requireUser: false });
  }

  async registerFirstAdmin(input: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    role: Role;
  }): Promise<void> {
    await this.request('/v1/auth/register-first-admin', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        nom: input.nom,
        prenom: input.prenom,
        role: input.role,
      }),
      requireUser: false,
    });
  }

  /** Navigateur Cloud : vérifie une clé de licence (valide, entreprise sans administrateur ou non). */
  async webActivate(licenseKey: string): Promise<{
    needs_first_admin: boolean;
    license_status: string;
    tier?: string;
    license_expires_at?: string;
  }> {
    return this.request(
      '/v1/auth/web/activate',
      { method: 'POST', body: JSON.stringify({ license_key: licenseKey }), requireUser: false },
      { skipRefreshRetry: true, quietLicence: true },
    );
  }

  /** Navigateur Cloud : premier administrateur, uniquement avec une licence valide (refus fermé côté serveur). */
  async webRegisterFirstAdmin(input: {
    licenseKey: string;
    email: string;
    password: string;
    nom: string;
    prenom: string;
  }): Promise<void> {
    await this.request(
      '/v1/auth/web/first-admin',
      {
        method: 'POST',
        body: JSON.stringify({
          license_key: input.licenseKey,
          email: input.email,
          password: input.password,
          nom: input.nom,
          prenom: input.prenom,
          role: 'admin',
        }),
        requireUser: false,
      },
      { skipRefreshRetry: true, quietLicence: true },
    );
  }

  async loginUser(
    email: string,
    password: string,
    licenseKey?: string,
    tenantId?: string,
  ): Promise<{
    token: string;
    expires_at: string;
    user: RemoteUser;
    entitlements_jwt?: string;
    companies?: WebCompany[];
  }> {
    const res = await this.request<{
      token: string;
      expires_at: string;
      user: RemoteUser;
      entitlements_jwt?: string;
      companies?: WebCompany[];
    }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        ...(licenseKey ? { license_key: licenseKey } : {}),
        ...(tenantId ? { tenant_id: tenantId } : {}),
      }),
      requireUser: false,
    });
    /**
     * Cookie httpOnly = source de vérité navigateur (C1-REGRESSION).
     * Bearer mémoire uniquement Electron / hors-fenêtre (tests Node) — pas de JWT
     * rejouable après XSS sur le SPA Cloud.
     */
    this.acknowledgeUserSession();
    this.rememberSessionExpiry(res.expires_at);
    const keepBearerMemory = typeof window === 'undefined' || Boolean(window.electron);
    if (keepBearerMemory && res.token) {
      this.setUserToken(res.token);
    } else {
      this.setUserToken(null);
      this.acknowledgeUserSession();
    }
    return res;
  }

  /**
   * Mot de passe oublié. Réponse du serveur toujours identique (compte inconnu ou non) ; en navigateur Cloud la clé
   * de licence, si elle est saisie, désigne l'entreprise (e-mail présent dans plusieurs entreprises).
   */
  async requestPasswordReset(email: string, licenseKey?: string): Promise<void> {
    await this.request(
      '/v1/auth/password-reset/request',
      {
        method: 'POST',
        body: JSON.stringify({ email, ...(licenseKey ? { license_key: licenseKey } : {}) }),
        requireUser: false,
      },
      { skipRefreshRetry: true },
    );
  }

  async logoutUser(): Promise<void> {
    this.cancelSessionRefresh();
    try {
      await this.request('/v1/auth/logout', { method: 'POST', requireUser: true });
    } finally {
      this.setUserToken(null);
    }
  }

  async fetchMe(): Promise<RemoteUser | null> {
    return (await this.fetchSession())?.user ?? null;
  }

  /** Session courante + droits signés (`entitlements_jwt`) quand le serveur les livre. */
  async fetchSession(): Promise<{
    user: RemoteUser;
    entitlements_jwt?: string;
    installation?: RemoteInstallationInfo;
  } | null> {
    try {
      const res = await this.request<{
        user: RemoteUser;
        entitlements_jwt?: string;
        installation?: RemoteInstallationInfo;
        session?: { expires_at?: string };
      }>('/v1/auth/me', {
        method: 'GET',
        requireUser: true,
      });
      this.acknowledgeUserSession();
      this.rememberSessionExpiry(res.session?.expires_at);
      return res;
    } catch {
      return null;
    }
  }

  async listUsers(): Promise<RemoteUserRecord[]> {
    const res = await this.request<{ items: RemoteUserRecord[] }>('/v1/users', {
      method: 'GET',
      requireUser: true,
    });
    return res.items;
  }

  async createUser(input: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    role: Role;
  }): Promise<RemoteUserRecord> {
    const res = await this.request<{ user: RemoteUserRecord }>('/v1/users', {
      method: 'POST',
      body: JSON.stringify(input),
      requireUser: true,
    });
    return res.user;
  }

  async resetUserPassword(userId: string, password: string): Promise<void> {
    await this.request(`/v1/users/${encodeURIComponent(userId)}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ password }),
      requireUser: true,
    });
  }

  async updateUser(
    userId: string,
    patch: { role?: Role; active?: boolean; salarie_id?: string | null },
  ): Promise<RemoteUserRecord> {
    const res = await this.request<{ user: RemoteUserRecord }>(
      `/v1/users/${encodeURIComponent(userId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
        requireUser: true,
      },
    );
    return res.user;
  }

  async listAuditLogs(): Promise<RemoteAuditLogRecord[]> {
    const res = await this.request<{ items: RemoteAuditLogRecord[] }>('/v1/audit-logs', {
      method: 'GET',
      requireUser: true,
    });
    return res.items;
  }

  async createAuditLog(input: {
    action: string;
    resource_type: string;
    resource_id?: string;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    await this.request('/v1/audit-logs', {
      method: 'POST',
      body: JSON.stringify(input),
      requireUser: true,
    });
  }

  async getTenantProfile(): Promise<RemoteVersionedPayload | null> {
    try {
      return await this.request<RemoteVersionedPayload>('/v1/tenant-profile', {
        method: 'GET',
        requireUser: true,
      });
    } catch (err) {
      if (err instanceof BusinessApiError && err.statusCode === 404) return null;
      throw err;
    }
  }

  async upsertTenantProfile(
    payload: Record<string, unknown>,
    version: number,
  ): Promise<RemoteVersionedPayload> {
    try {
      return await this.request<RemoteVersionedPayload>('/v1/tenant-profile', {
        method: 'PUT',
        body: JSON.stringify({ payload, version }),
        requireUser: true,
      });
    } catch (err) {
      // Même règle que updateEntity : seul un vrai conflit de version devient un conflit.
      if (
        err instanceof BusinessApiError &&
        err.statusCode === 409 &&
        (err.retry === undefined || err.retry === 'conflict')
      ) {
        throw new BusinessApiConflictError();
      }
      throw err;
    }
  }

  async listModuleData(): Promise<RemoteModuleDataRecord[]> {
    const res = await this.request<{ items: RemoteModuleDataRecord[] }>('/v1/module-data', {
      method: 'GET',
      requireUser: true,
    });
    return res.items ?? [];
  }

  async getModuleData(namespace: string): Promise<RemoteModuleDataRecord | null> {
    try {
      return await this.request<RemoteModuleDataRecord>(
        `/v1/module-data/${encodeURIComponent(namespace)}`,
        {
          method: 'GET',
          requireUser: true,
        },
      );
    } catch (err) {
      if (err instanceof BusinessApiError && err.statusCode === 404) return null;
      throw err;
    }
  }

  async upsertModuleData(
    namespace: string,
    payload: Record<string, unknown>,
    version: number,
  ): Promise<RemoteModuleDataRecord> {
    try {
      return await this.request<RemoteModuleDataRecord>(
        `/v1/module-data/${encodeURIComponent(namespace)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ payload, version }),
          requireUser: true,
        },
      );
    } catch (err) {
      // Même règle que le profil entreprise : seul un vrai conflit de version devient un conflit.
      if (
        err instanceof BusinessApiError &&
        err.statusCode === 409 &&
        (err.retry === undefined || err.retry === 'conflict')
      ) {
        throw new BusinessApiConflictError();
      }
      throw err;
    }
  }

  async listEntities(resource: string): Promise<ApiEntityRow[]> {
    const res = await this.request<{ items: ApiEntityRow[] }>(`/v1/${resource}`, {
      method: 'GET',
      requireUser: true,
    });
    return res.items;
  }

  async getEntity(resource: string, id: string): Promise<ApiEntityRow | null> {
    try {
      return await this.request<ApiEntityRow>(`/v1/${resource}/${id}`, {
        method: 'GET',
        requireUser: true,
      });
    } catch (err) {
      if (err instanceof BusinessApiError && err.statusCode === 404) return null;
      throw err;
    }
  }

  async createEntity(resource: string, payload: Record<string, unknown>): Promise<ApiEntityRow> {
    return this.request<ApiEntityRow>(`/v1/${resource}`, {
      method: 'POST',
      body: JSON.stringify({ payload }),
      requireUser: true,
    });
  }

  async updateEntity(
    resource: string,
    id: string,
    payload: Record<string, unknown>,
    version: number,
  ): Promise<ApiEntityRow> {
    try {
      return await this.request<ApiEntityRow>(`/v1/${resource}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ payload, version }),
        requireUser: true,
      });
    } catch (err) {
      // Seul un conflit de version se traduit en conflit : les refus permanents en 409 (plafond de salariés,
      // bulletin verrouillé `payslip_locked`) gardent leur code et leur classe de reprise.
      if (
        err instanceof BusinessApiError &&
        err.statusCode === 409 &&
        (err.retry === undefined || err.retry === 'conflict')
      ) {
        throw new BusinessApiConflictError();
      }
      throw err;
    }
  }

  async deleteEntity(resource: string, id: string): Promise<void> {
    await this.request(`/v1/${resource}/${id}`, { method: 'DELETE', requireUser: true });
  }

  async listCoffrefortDocuments(): Promise<RemoteCoffrefortDocument[]> {
    const res = await this.request<{ items: RemoteCoffrefortDocument[] }>(
      '/v1/coffrefort/documents',
      {
        method: 'GET',
        requireUser: true,
      },
    );
    return res.items;
  }

  async uploadCoffrefortDocument(
    file: File,
    meta: { scope: string; salarie_id: string | null; category: string },
  ): Promise<RemoteCoffrefortDocument> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('scope', meta.scope);
    if (meta.salarie_id) form.append('salarie_id', meta.salarie_id);
    form.append('category', meta.category);
    return this.uploadRequest<RemoteCoffrefortDocument>('/v1/coffrefort/documents', form);
  }

  async downloadCoffrefortDocument(id: string): Promise<Blob> {
    return this.downloadRequest(`/v1/coffrefort/documents/${encodeURIComponent(id)}/content`);
  }

  async deleteCoffrefortDocument(id: string): Promise<void> {
    await this.request(`/v1/coffrefort/documents/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      requireUser: true,
    });
  }

  private buildAuthHeaders(
    installation: string | null,
    extra?: Record<string, string>,
    method = 'GET',
  ): Record<string, string> {
    const headers: Record<string, string> = { ...extra };
    if (installation) {
      headers['X-Installation-Token'] = installation;
    } else {
      // Cloud navigateur : cookie httpOnly + en-tête CSRF pour toute écriture.
      headers[SESSION_MODE_HEADER] = 'cookie';
      if (!SAFE_METHODS.has(method.toUpperCase())) {
        const csrf = BusinessApiClient.readCsrfToken();
        if (csrf) headers['X-CSRF-Token'] = csrf;
      }
    }
    /** Bearer seulement si jeton mémoire (cookie httpOnly part déjà via credentials). */
    if (this.memoryUserToken) {
      headers['Authorization'] = `Bearer ${this.memoryUserToken}`;
    }
    return headers;
  }

  /**
   * Jeton d'installation du poste (Electron) ; `null` pour le navigateur Cloud, dont la session est un cookie
   * httpOnly et qui n'a ni certificat ni jeton d'installation.
   */
  private async installationFor(): Promise<string | null> {
    if (isBrowserCloudHost()) return null;
    const installation = this.overrides.installationToken
      ? await this.overrides.installationToken()
      : await SecureLicenceStore.getInstallationSessionToken();
    if (!installation) throw new BusinessApiError('Session installation absente');
    return installation;
  }

  private async uploadRequest<T>(path: string, form: FormData): Promise<T> {
    const base = this.getBaseUrl();
    if (!base) throw new BusinessApiError('URL API métier non configurée');
    const installation = await this.installationFor();

    const res = await resilientFetch(
      `${base}${path}`,
      {
        method: 'POST',
        headers: this.buildAuthHeaders(installation, undefined, 'POST'),
        body: form,
        credentials: 'include',
      },
      { timeoutMs: 60_000 },
    );

    if (!res.ok) {
      throw new BusinessApiError(`HTTP ${res.status}`, res.status);
    }
    return (await res.json()) as T;
  }

  private async downloadRequest(path: string): Promise<Blob> {
    return (await this.downloadWithName(path)).blob;
  }

  /** Téléchargement d'un fichier avec son nom (en-tête `Content-Disposition`), pour les exports. */
  private async downloadWithName(path: string): Promise<{ blob: Blob; filename: string | null }> {
    const base = this.getBaseUrl();
    if (!base) throw new BusinessApiError('URL API métier non configurée');
    const installation = await this.installationFor();

    const res = await resilientFetch(
      `${base}${path}`,
      {
        method: 'GET',
        headers: this.buildAuthHeaders(installation),
        credentials: 'include',
      },
      { timeoutMs: 60_000 },
    );
    if (!res.ok) throw new BusinessApiError(`HTTP ${res.status}`, res.status);
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] ?? null };
  }

  /**
   * Export de portabilité RGPD de toute l'entreprise (administrateur, Cloud) : `GET /v1/export/full`.
   * Le serveur journalise l'export et limite le débit (10 par quart d'heure).
   */
  async exportRgpdComplet(format: 'json' | 'csv'): Promise<{ blob: Blob; filename: string }> {
    const r = await this.downloadWithName(`/v1/export/full?format=${format}`);
    return { blob: r.blob, filename: r.filename ?? `nexxus-export-${aujourdhuiIso()}.${format}` };
  }

  /** Export de portabilité d'une personne (fiche salarié) : `GET /v1/export/person/:id`. */
  async exportRgpdPersonne(id: string, format: 'json' | 'csv'): Promise<{ blob: Blob; filename: string }> {
    const r = await this.downloadWithName(`/v1/export/person/${encodeURIComponent(id)}?format=${format}`);
    return { blob: r.blob, filename: r.filename ?? `nexxus-export-personne-${aujourdhuiIso()}.${format}` };
  }

  private async request<T = unknown>(
    path: string,
    init: RequestInit & { requireUser: boolean },
    opts?: { skipRefreshRetry?: boolean; quietLicence?: boolean },
  ): Promise<T> {
    const base = this.getBaseUrl();
    if (!base) throw new BusinessApiError('URL API métier non configurée');

    const installation = await this.installationFor();

    // Content-Type seulement avec un corps : Fastify refuse un corps JSON vide (400) sur POST sans données.
    const headers = this.buildAuthHeaders(
      installation,
      init.body === undefined || init.body === null ? {} : { 'Content-Type': 'application/json' },
      init.method ?? 'GET',
    );

    const res = await resilientFetch(
      `${base}${path}`,
      { ...init, headers, credentials: 'include' },
      { timeoutMs: 12_000 },
    );

    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      let code: string | undefined;
      let retry: ApiRetryClass | undefined;
      let details: Record<string, unknown> | undefined;
      try {
        const errBody = (await res.json()) as {
          error?: string;
          code?: string;
          retry?: string;
          companies?: unknown;
        };
        if (errBody.error) message = errBody.error;
        if (Array.isArray(errBody.companies)) details = { companies: errBody.companies };
        if (errBody.code) code = errBody.code;
        if (
          errBody.retry === 'permanent' ||
          errBody.retry === 'transient' ||
          errBody.retry === 'conflict'
        ) {
          retry = errBody.retry;
        }
      } catch {
        /* ignore */
      }

      // AUTH-REFRESH : access expiré → rotation cookie refresh puis 1 retry
      if (
        res.status === 401 &&
        init.requireUser &&
        !opts?.skipRefreshRetry &&
        path !== '/v1/auth/refresh' &&
        path !== '/v1/auth/login' &&
        path !== '/v1/auth/logout' &&
        !INSTALLATION_AUTH_CODES.has(code ?? '')
      ) {
        const refreshed = await this.tryRefreshSession();
        if (refreshed) {
          return this.request<T>(path, init, { skipRefreshRetry: true });
        }
      }

      // Cloud navigateur : plus de session (expirée, révoquée, cookie absent) -> retour à la connexion.
      if (
        res.status === 401 &&
        init.requireUser &&
        isBrowserCloudHost() &&
        path !== '/v1/auth/logout' &&
        SESSION_GONE_CODES.has(code ?? '') &&
        typeof window !== 'undefined'
      ) {
        window.dispatchEvent(new CustomEvent(CLOUD_SESSION_EXPIRED_EVENT, { detail: { code } }));
      }

      const clear = cloudLicenceMessage(code);
      if (clear) {
        message = clear;
        // Activation web : un refus de licence est une réponse de l'écran, pas une bascule en lecture seule.
        if (isCloudRestrictionCode(code) && !opts?.quietLicence && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(CLOUD_LICENCE_REFUSAL_EVENT, { detail: { code } }));
        }
      }
      throw new BusinessApiError(message, res.status, code, retry, details);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /**
   * Requête du protocole de synchronisation Hybride (`/v1/sync/*`). Retourne le JSON brut ; toute défaillance est
   * typée (réseau, délai dépassé, HTTP avec code stable et `Retry-After`) pour que le moteur choisisse la bonne
   * réaction. Pas de renouvellement de session ici : le moteur le demande explicitement (`refreshSessionForSync`).
   */
  async syncRequest(
    method: 'GET' | 'POST',
    path: string,
    opts: { body?: unknown; timeoutMs: number },
  ): Promise<unknown> {
    const base = this.getBaseUrl();
    if (!base) throw new SyncRequestError('URL API métier non configurée', 'network');
    let installation: string | null;
    try {
      installation = await this.installationFor();
    } catch {
      throw new SyncRequestError('Session installation absente', 'http', 401, 'installation_token_required', 'transient');
    }
    const headers = this.buildAuthHeaders(
      installation,
      {
        [SYNC_PROTOCOL_HEADER]: String(SYNC_PROTOCOL_VERSION),
        ...(opts.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      method,
    );
    let res: Response;
    try {
      res = await resilientFetch(
        `${base}${path}`,
        {
          method,
          headers,
          credentials: 'include',
          ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
        },
        { timeoutMs: opts.timeoutMs },
      );
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      const kind = name === 'TimeoutError' || name === 'AbortError' ? 'timeout' : 'network';
      throw new SyncRequestError(kind === 'timeout' ? 'Délai dépassé' : 'Serveur injoignable', kind);
    }
    if (!res.ok) {
      let code: string | undefined;
      let retry: ApiRetryClass | undefined;
      let message = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { error?: string; code?: string; retry?: string };
        if (body.error) message = body.error;
        if (body.code) code = body.code;
        if (body.retry === 'permanent' || body.retry === 'transient' || body.retry === 'conflict') retry = body.retry;
      } catch {
        /* corps non JSON (proxy) : le statut HTTP suffit */
      }
      const header = res.headers.get('Retry-After');
      let retryAfterMs: number | undefined;
      if (header) {
        const seconds = Number(header);
        if (Number.isFinite(seconds)) retryAfterMs = Math.max(0, seconds * 1000);
        else {
          const at = Date.parse(header);
          if (Number.isFinite(at)) retryAfterMs = Math.max(0, at - Date.now());
        }
      }
      throw new SyncRequestError(message, 'http', res.status, code, retry, retryAfterMs);
    }
    if (res.status === 204) return {};
    try {
      return await res.json();
    } catch {
      throw new SyncRequestError('Réponse illisible', 'protocol', res.status);
    }
  }

  /** Renouvelle la session utilisateur à la demande du moteur de synchronisation. */
  async refreshSessionForSync(): Promise<boolean> {
    return this.tryRefreshSession();
  }

  /** POST /v1/auth/refresh — cookies httpOnly ; met à jour le Bearer mémoire si présent. */
  private async tryRefreshSession(): Promise<boolean> {
    return (await this.refreshSession()) === 'ok';
  }

  /** `expired` : le serveur refuse le renouvellement (session expirée, durée maximale atteinte, compte désactivé). */
  private async refreshSession(): Promise<'ok' | 'expired' | 'unreachable'> {
    try {
      const result = await this.request<{
        token?: string;
        expires_at?: string;
        user: RemoteUser;
      }>(
        '/v1/auth/refresh',
        { method: 'POST', body: '{}', requireUser: false },
        {
          skipRefreshRetry: true,
        },
      );
      if (result.token) {
        this.memoryUserToken = result.token;
      }
      this.acknowledgeUserSession();
      this.rememberSessionExpiry(result.expires_at);
      return 'ok';
    } catch (err) {
      return err instanceof BusinessApiError && err.statusCode === 401 ? 'expired' : 'unreachable';
    }
  }

  /**
   * Cloud navigateur : la session est renouvelée avant son échéance (à 70 % de la durée restante, et dès que
   * l'onglet redevient visible si l'échéance planifiée est passée), sans action de l'utilisateur.
   */
  private rememberSessionExpiry(expiresAtIso: string | undefined): void {
    if (!isBrowserCloudHost() || typeof window === 'undefined' || !expiresAtIso) return;
    const at = Date.parse(expiresAtIso);
    if (!Number.isFinite(at)) return;
    this.sessionExpiresAt = at;
    this.scheduleSessionRefresh(Math.max(1_000, Math.floor((at - Date.now()) * 0.7)));
    if (!this.visibilityWired && typeof document !== 'undefined') {
      this.visibilityWired = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.refreshTimer && Date.now() >= this.refreshDueAt) {
          void this.runScheduledRefresh();
        }
      });
    }
  }

  private scheduleSessionRefresh(delayMs: number): void {
    this.cancelSessionRefresh();
    this.refreshDueAt = Date.now() + delayMs;
    this.refreshTimer = setTimeout(() => void this.runScheduledRefresh(), delayMs);
  }

  private cancelSessionRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private async runScheduledRefresh(): Promise<void> {
    this.cancelSessionRefresh();
    const outcome = await this.refreshSession();
    if (outcome === 'ok') return; // rememberSessionExpiry a replanifié
    if (outcome === 'expired' || Date.now() >= this.sessionExpiresAt) {
      logger.warn({ event: 'cloud_session_refresh_denied' });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CLOUD_SESSION_EXPIRED_EVENT, { detail: { code: 'session_invalid' } }));
      }
      return;
    }
    // Serveur injoignable : nouvel essai tant que la session n'a pas expiré.
    this.scheduleSessionRefresh(15_000);
  }
}

let sharedClient: BusinessApiClient | null = null;

export function getBusinessApiClient(): BusinessApiClient {
  if (!sharedClient) {
    sharedClient = new BusinessApiClient();
    BusinessApiClient.wireNetworkListener(sharedClient);
  }
  return sharedClient;
}

export function setBusinessApiClientForTests(client: BusinessApiClient | null): void {
  sharedClient = client;
}
